import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowRight, PlusCircle } from "lucide-react";
import { getMyProcesses } from "@/lib/portalApi";
import { ProgressBar } from "@/components/account/ProgressBar";
import { StatusBadge } from "@/components/portal/StatusBadge";
import { formatDateTime } from "@/components/portal/portalConstants";
import { Button } from "@/components/ui/button";

export const CustomerDashboard = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [processes, setProcesses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    getMyProcesses()
      .then(setProcesses)
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="grid place-items-center py-20 text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customer-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t("account.dashboard.title")}</h1>
          <p className="text-sm text-slate-400">{t("account.dashboard.subtitle")}</p>
        </div>
        <Button onClick={() => navigate("/apply")} variant="outline" className="border-slate-700 bg-slate-900 hover:bg-slate-800" data-testid="dashboard-new-application">
          <PlusCircle className="h-4 w-4 mr-1.5" /> {t("account.dashboard.apply")}
        </Button>
      </div>

      {processes.length === 0 && (
        <div className="rounded-2xl border border-slate-800 bg-slate-900/40 p-10 text-center" data-testid="dashboard-empty">
          <p className="text-slate-300">{t("account.dashboard.empty")}</p>
          <Button onClick={() => navigate("/apply")} className="mt-4 rounded-full bg-venka-green-light text-slate-950 hover:bg-venka-green-light font-semibold">
            {t("account.dashboard.apply")}
          </Button>
        </div>
      )}

      <div className="grid gap-4">
        {processes.map((p) => (
          <button
            key={p.id}
            onClick={() => navigate(`/account/processes/${p.id}`)}
            className="text-left rounded-2xl border border-slate-800 bg-slate-900/40 p-5 hover:border-slate-600 transition-colors"
            data-testid={`dashboard-process-${p.id}`}
          >
            <div className="flex items-start justify-between gap-4">
              <div>
                <h2 className="font-semibold">{p.process_type_name}</h2>
                <p className="text-sm text-slate-400">
                  {p.country_destination || "—"} · {t("account.dashboard.reference")}: <span className="font-mono text-xs">{p.id.slice(0, 8)}</span>
                </p>
              </div>
              <StatusBadge status={p.status} />
            </div>
            <div className="mt-5 mb-1">
              <ProgressBar status={p.status} />
            </div>
            <div className="flex items-center justify-between mt-3">
              <span className="text-xs text-slate-500">{t("account.detail.updated")}: {formatDateTime(p.updated_at, i18n.resolvedLanguage)}</span>
              <span className="flex items-center gap-1 text-xs text-venka-green-light">{t("account.dashboard.viewDetails")} <ArrowRight className="h-3 w-3" /></span>
            </div>
          </button>
        ))}
      </div>
    </div>
  );
};

export default CustomerDashboard;
